/**
 * Hook that backs Schedule column-filter state with `localStorage`, keyed
 * per view (Classic / Pro / Positions). Eliminates the "I just had that
 * filter set up!" annoyance when the user toggles between views or reloads
 * the page.
 *
 * Failure modes are all silent (Safari private mode throws, quota overflow,
 * corrupted JSON) — every catch falls back to an empty filter set. The
 * UI never sees an error from preference persistence.
 */
import React from 'react';
import {
  type ScheduleColumnFilters,
  emptyScheduleFilters,
  serializeScheduleFilters,
  deserializeScheduleFilters,
} from '../utils/scheduleFilters';

function readFromStorage(storageKey: string): ScheduleColumnFilters {
  if (typeof window === 'undefined') return emptyScheduleFilters();
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return emptyScheduleFilters();
    return deserializeScheduleFilters(JSON.parse(raw));
  } catch {
    return emptyScheduleFilters();
  }
}

function writeToStorage(storageKey: string, filters: ScheduleColumnFilters): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(serializeScheduleFilters(filters)));
  } catch {
    // Safari private mode + quota overflow — non-fatal for prefs.
  }
}

/**
 * Drop-in replacement for `React.useState<ScheduleColumnFilters>(emptyScheduleFilters)`
 * that hydrates from localStorage on first mount and writes through on every
 * subsequent update.
 *
 * Usage:
 *   const [columnFilters, setColumnFilters] = usePersistedScheduleFilters(
 *     'schedule:classic:filters'
 *   );
 */
export function usePersistedScheduleFilters(
  storageKey: string,
): [ScheduleColumnFilters, React.Dispatch<React.SetStateAction<ScheduleColumnFilters>>] {
  const [filters, setFilters] = React.useState<ScheduleColumnFilters>(() =>
    readFromStorage(storageKey),
  );

  React.useEffect(() => {
    writeToStorage(storageKey, filters);
  }, [storageKey, filters]);

  return [filters, setFilters];
}
